import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Theme,
    makeStyles,
} from '@material-ui/core';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Save } from 'react-feather';
import useSource from 'src/hooks/useSource';
import useStore from 'src/hooks/useStore';
import useAuth from 'src/hooks/useAuth';
import useMake from 'src/hooks/useMake';
import useVehicle from 'src/hooks/useVehicle';
import { CapitalizeNames } from 'src/utils/capitalize';
import { IStore } from 'src/interfaces/store';
import { ISource } from 'src/interfaces/source';
import { IVehicle } from 'src/interfaces/vehicle';
import { SOURCE_SERVICE_AREA } from 'src/constants/source/areas';
import { useServiceClient } from 'src/hooks';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import {
    IServiceClientFormProps,
    TServiceClientFormValues,
    defaultServiceClientFormValues,
} from './types';
import { ServiceClientVehiclesCard } from './ServiceClientVehiclesCard';
import { AreaCodeTextField } from 'src/modules/forms/fields/AreaCodeTextField/AreaCodeTextField';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';

const useStyles = makeStyles((theme: Theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    submitButton: {
        minWidth: 150,
        height: 56,
    },
}));

export const ServiceClientForm = ({
    mode,
    initialValues = defaultServiceClientFormValues,
    onSubmit,
}: IServiceClientFormProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { error } = useServiceClient();
    const { sources, getSourcesV2 } = useSource();
    const { stores, getStores } = useStore();
    const { makes, getMakes } = useMake();
    const {
        vehicles,
        getVehiclesAdvancedResults,
        clearState: clearVehicles,
    } = useVehicle();
    const [vehiclesByMake, setVehiclesByMake] = useState<Record<string, IVehicle[]>>({});
    const lastRequestedMakeIdRef = useRef<string>('');

    const fetchVehiclesForMake = useCallback(
        (makeId: string) => {
            if (!makeId) return;
            lastRequestedMakeIdRef.current = makeId;
            getVehiclesAdvancedResults({
                filters: { make: makeId, isActive: true },
                select: 'make model modelType',
                populate: 'make',
                sort: 'model',
                limit: 200,
            });
        },
        [getVehiclesAdvancedResults]
    );

    useEffect(() => {
        if (!user?._id) return;
        getMakes({ select: 'name' });
        getSourcesV2({
            filters: { isActive: true, areas: [SOURCE_SERVICE_AREA] },
        });
        getStores();
    }, [user]);

    useEffect(() => {
        if (vehicles?.length && lastRequestedMakeIdRef.current) {
            const makeId = lastRequestedMakeIdRef.current;
            setVehiclesByMake((prev) => ({ ...prev, [makeId]: vehicles }));
        }
    }, [vehicles]);

    const CURRENT_YEAR = Number(moment().format('yyyy'));

    const validationSchema = Yup.object({
        name: Yup.string().required(t('Yup.Required')).max(80, t('Yup.MaxLength')),
        email: Yup.string()
            .email(t('Yup.Email'))
            .required(t('Yup.Required')),
        phone: Yup.string()
            .matches(/^[0-9]+$/, t('Yup.Number'))
            .test('len', t('Yup.Phone'), (val: string | undefined) =>
                Boolean(val && val.length === 10)
            ),
        source: Yup.string().required(t('Yup.Required')),
        store: Yup.string().required(t('Yup.Required')),
    });

    const initialFormValues: TServiceClientFormValues = {
        ...defaultServiceClientFormValues,
        ...initialValues,
        source: getDocumentId(initialValues.source) ?? defaultServiceClientFormValues.source,
        store: getDocumentId(initialValues.store) ?? defaultServiceClientFormValues.store,
        countryCode: initialValues.countryCode ?? defaultServiceClientFormValues.countryCode,
        service: {
            vehicles: Array.isArray(initialValues.service?.vehicles)
                ? (initialValues.service?.vehicles ?? [])
                : [],
        },
    };

    return (
        <Formik
            initialValues={initialFormValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {(formikProps: FormikProps<TServiceClientFormValues>) => {
                const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formikProps;
                return (
                    <Form data-testid="service-client-form">
                        <Card elevation={0}>
                            <CardHeader
                                title={
                                    <span data-testid="service-client-form-title">
                                        {mode === 'create'
                                            ? String(t('serviceClients.Create'))
                                            : String(t('serviceClients.Edit'))}
                                    </span>
                                }
                            />
                            <Divider />
                            <CardContent className={classes.formContainer}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={`${t('serviceClients.name')}`}
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                            inputProps={{ 'data-testid': 'service-client-form-name' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            label={`${t('serviceClients.email')}`}
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email}
                                            inputProps={{ 'data-testid': 'service-client-form-email' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <AreaCodeTextField
                                            name="phone"
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            handleChange={handleChange}
                                            props={{
                                                variant: 'outlined',
                                                fullWidth: true,
                                                label: String(t('serviceClients.phone')),
                                                required: true,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={touched.source && Boolean(errors.source)}
                                        >
                                            <InputLabel>{String(t('serviceClients.source'))}</InputLabel>
                                            <Select
                                                name="source"
                                                value={values.source}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label={`${t('serviceClients.source')}`}
                                            >
                                                {sources.map((src: ISource) => (
                                                    <MenuItem key={src._id} value={src._id}>
                                                        {CapitalizeNames(src.name)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>{touched.source && errors.source}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            error={touched.store && Boolean(errors.store)}
                                        >
                                            <InputLabel>{String(t('serviceClients.agency'))}</InputLabel>
                                            <Select
                                                name="store"
                                                value={values.store}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label={`${t('serviceClients.agency')}`}
                                            >
                                                {stores.map((store: IStore) => (
                                                    <MenuItem key={store._id} value={store._id}>
                                                        {CapitalizeNames(store.name)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>{touched.store && errors.store}</FormHelperText>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                {
                                    mode === 'create' && (
                                        <ServiceClientVehiclesCard
                                            vehicles={values.service.vehicles}
                                            onVehiclesChange={(next) => setFieldValue('service.vehicles', next)}
                                            onBlur={handleBlur}
                                            vehiclesByMake={vehiclesByMake}
                                            onFetchVehiclesForMake={fetchVehiclesForMake}
                                            onClearVehicles={clearVehicles}
                                            makes={makes}
                                            currentYear={CURRENT_YEAR}
                                        />)
                                }

                                {error && (
                                    <Alert severity="error" style={{ marginTop: 16 }}>
                                        {error}
                                    </Alert>
                                )}

                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                    <Button
                                        data-testid="service-client-form-submit"
                                        type="submit"
                                        color="primary"
                                        variant="contained"
                                        startIcon={<Save />}
                                        className={classes.submitButton}
                                    >
                                        {mode === 'create'
                                            ? String(t('serviceClients.Create'))
                                            : String(t('Buttons.Update'))}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Form>
                );
            }}
        </Formik>
    );
};
